import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * OrganizationalUnitService - Manages organizational teams (departments, year groups, committees, etc.)
 *
 * Core principles:
 * - Aggressive caching for team structure (rarely changes)
 * - School-scoped operations
 * - Denormalized stats for performance
 * - Hierarchical relationship management
 */
class OrganizationalUnitService {
  constructor() {
    this.cache = {
      unitsBySchool: new Map(), // schoolId -> teams array
      timestamps: new Map(),    // schoolId -> timestamp
      ttl: 300000 // 5 minutes cache (team structure changes infrequently)
    };
  }

  /**
   * Get all organizational teams for a school with caching
   *
   * @param {string} schoolId - School ID
   * @param {boolean} forceRefresh - Force bypass cache
   * @returns {Promise<Array>} Array of organizational team objects
   */
  async getUnits(schoolId, forceRefresh = false) {
    if (!schoolId) {
      throw new Error('schoolId is required');
    }

    const now = Date.now();
    const cached = this.cache.unitsBySchool.get(schoolId);
    const timestamp = this.cache.timestamps.get(schoolId);

    // Return cached data if still valid
    if (!forceRefresh && cached && timestamp && (now - timestamp) < this.cache.ttl) {
      console.log(`[OrgUnitService] Returning cached units for school ${schoolId}`);
      return cached;
    }

    try {
      console.log(`[OrgUnitService] Fetching units from Firestore for school ${schoolId}`);
      const unitsRef = collection(db, 'schools', schoolId, 'organizationalUnits');
      // Simplified query to avoid composite index requirement - filter and sort client-side
      const q = query(
        unitsRef,
        where('active', '==', true)
      );

      const snapshot = await getDocs(q);
      const units = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        // Sort by name client-side
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      // Update cache
      this.cache.unitsBySchool.set(schoolId, units);
      this.cache.timestamps.set(schoolId, now);

      console.log(`[OrgUnitService] Fetched ${units.length} units for school ${schoolId}`);
      return units;
    } catch (error) {
      console.error('[OrgUnitService] Error fetching units:', error);
      throw error;
    }
  }

  /**
   * Get a single organizational team by ID
   *
   * @param {string} unitId - Team ID
   * @returns {Promise<Object>} Organizational team object
   */
  async getUnit(unitId, schoolId) {
    if (!unitId || !schoolId) {
      throw new Error('unitId and schoolId are required');
    }

    try {
      const unitRef = doc(db, 'schools', schoolId, 'organizationalUnits', unitId);
      const snapshot = await getDoc(unitRef);

      if (!snapshot.exists()) {
        throw new Error(`Unit ${unitId} not found`);
      }

      return {
        id: snapshot.id,
        ...snapshot.data()
      };
    } catch (error) {
      console.error(`[OrgUnitService] Error fetching unit ${unitId}:`, error);
      throw error;
    }
  }

  /**
   * Get teams by type for a school
   *
   * @param {string} schoolId - School ID
   * @param {string} type - Team type (department, yearGroup, pastoralTeam, committee, custom)
   * @returns {Promise<Array>} Filtered teams
   */
  async getUnitsByType(schoolId, type) {
    const units = await this.getUnits(schoolId);
    return units.filter(unit => unit.type === type);
  }

  /**
   * Get child teams (teams with specific parent)
   *
   * @param {string} schoolId - School ID
   * @param {string} parentUnitId - Parent team ID
   * @returns {Promise<Array>} Child teams
   */
  async getChildUnits(schoolId, parentUnitId) {
    const units = await this.getUnits(schoolId);
    return units.filter(unit => unit.parentUnitId === parentUnitId);
  }

  /**
   * Create a new organizational team
   *
   * @param {Object} unitData - Team data
   * @param {string} createdBy - User ID creating the team
   * @returns {Promise<Object>} Created team with ID
   */
  async createUnit(unitData, createdBy) {
    try {
      console.log('[OrgUnitService] Creating new unit:', unitData.name);

      // Validate required fields
      if (!unitData.schoolId) throw new Error('schoolId is required');
      if (!unitData.name) throw new Error('name is required');
      if (!unitData.type) throw new Error('type is required');

      const now = Timestamp.now();
      const newUnit = {
        ...unitData,
        active: unitData.active !== undefined ? unitData.active : true,
        stats: {
          memberCount: 0,
          studentCount: 0,
          classCount: 0,
          lastUpdated: now
        },
        createdAt: now,
        createdBy,
        updatedAt: now,
        updatedBy: createdBy
      };

      const unitsRef = collection(db, 'schools', unitData.schoolId, 'organizationalUnits');
      const docRef = await addDoc(unitsRef, newUnit);

      const createdUnit = {
        id: docRef.id,
        ...newUnit
      };

      // Invalidate cache for this school
      this.invalidateSchoolCache(unitData.schoolId);

      console.log(`[OrgUnitService] Created unit ${docRef.id}:`, unitData.name);
      return createdUnit;
    } catch (error) {
      console.error('[OrgUnitService] Error creating unit:', error);
      throw error;
    }
  }

  /**
   * Update an organizational team
   *
   * @param {string} unitId - Team ID
   * @param {Object} updates - Fields to update
   * @param {string} updatedBy - User ID making the update
   * @returns {Promise<boolean>} Success status
   */
  async updateUnit(unitId, updates, updatedBy, schoolId) {
    try {
      console.log(`[OrgUnitService] Updating unit ${unitId}`);

      if (!schoolId) {
        throw new Error('schoolId is required for updateUnit');
      }

      // Prevent updating system fields
      const { id, createdAt, createdBy, stats, ...safeUpdates } = updates;

      const unitRef = doc(db, 'schools', schoolId, 'organizationalUnits', unitId);
      await updateDoc(unitRef, {
        ...safeUpdates,
        updatedAt: Timestamp.now(),
        updatedBy
      });

      // Invalidate cache using the schoolId parameter
      this.invalidateSchoolCache(schoolId);

      console.log(`[OrgUnitService] Successfully updated unit ${unitId}`);
      return true;
    } catch (error) {
      console.error('[OrgUnitService] Error updating unit:', error);
      throw error;
    }
  }

  /**
   * Soft delete (archive) an organizational team
   *
   * @param {string} unitId - Team ID
   * @param {string} updatedBy - User ID archiving the team
   * @param {string} schoolId - School ID
   * @returns {Promise<boolean>} Success status
   */
  async archiveUnit(unitId, updatedBy, schoolId) {
    try {
      console.log(`[OrgUnitService] Archiving unit ${unitId}`);

      if (!schoolId) {
        throw new Error('schoolId is required for archiveUnit');
      }

      await this.updateUnit(unitId, { active: false }, updatedBy, schoolId);

      console.log(`[OrgUnitService] Successfully archived unit ${unitId}`);
      return true;
    } catch (error) {
      console.error('[OrgUnitService] Error archiving unit:', error);
      throw error;
    }
  }

  /**
   * Hard delete an organizational team (use with caution)
   *
   * @param {string} unitId - Team ID
   * @param {string} schoolId - School ID for cache invalidation
   * @returns {Promise<boolean>} Success status
   */
  async deleteUnit(unitId, schoolId) {
    try {
      console.log(`[OrgUnitService] Deleting unit ${unitId}`);

      const unitRef = doc(db, 'schools', schoolId, 'organizationalUnits', unitId);
      await deleteDoc(unitRef);

      // Invalidate cache
      this.invalidateSchoolCache(schoolId);

      console.log(`[OrgUnitService] Successfully deleted unit ${unitId}`);
      return true;
    } catch (error) {
      console.error('[OrgUnitService] Error deleting unit:', error);
      throw error;
    }
  }

  /**
   * Update team stats (member count, student count, etc.)
   *
   * @param {string} unitId - Team ID
   * @param {Object} stats - Stats to update
   * @returns {Promise<boolean>} Success status
   */
  async updateUnitStats(unitId, stats, schoolId) {
    try {
      if (!schoolId) {
        throw new Error('schoolId is required for updateUnitStats');
      }

      const unitRef = doc(db, 'schools', schoolId, 'organizationalUnits', unitId);
      await updateDoc(unitRef, {
        'stats.memberCount': stats.memberCount,
        'stats.studentCount': stats.studentCount,
        'stats.classCount': stats.classCount,
        'stats.lastUpdated': Timestamp.now()
      });

      return true;
    } catch (error) {
      console.error('[OrgUnitService] Error updating stats:', error);
      // Don't throw - stats updates are non-critical
      return false;
    }
  }

  /**
   * Batch create multiple teams (for setup wizard)
   *
   * @param {Array} unitsData - Array of team objects
   * @param {string} createdBy - User ID creating the teams
   * @returns {Promise<Array>} Created teams with IDs
   */
  async batchCreateUnits(unitsData, createdBy) {
    try {
      console.log(`[OrgUnitService] Batch creating ${unitsData.length} units`);

      const batch = writeBatch(db);
      const now = Timestamp.now();
      const createdUnits = [];

      unitsData.forEach(unitData => {
        if (!unitData.schoolId) {
          throw new Error('schoolId is required for each unit in batch');
        }

        const unitRef = doc(collection(db, 'schools', unitData.schoolId, 'organizationalUnits'));
        const newUnit = {
          ...unitData,
          active: unitData.active !== undefined ? unitData.active : true,
          stats: {
            memberCount: 0,
            studentCount: 0,
            classCount: 0,
            lastUpdated: now
          },
          createdAt: now,
          createdBy,
          updatedAt: now,
          updatedBy: createdBy
        };

        batch.set(unitRef, newUnit);
        createdUnits.push({
          id: unitRef.id,
          ...newUnit
        });
      });

      await batch.commit();

      // Invalidate cache for affected schools
      const schoolIds = [...new Set(unitsData.map(u => u.schoolId))];
      schoolIds.forEach(schoolId => this.invalidateSchoolCache(schoolId));

      console.log('[OrgUnitService] Batch create completed successfully');
      return createdUnits;
    } catch (error) {
      console.error('[OrgUnitService] Error in batch create:', error);
      throw error;
    }
  }

  /**
   * Build hierarchical tree structure from flat teams array
   *
   * @param {Array} units - Flat array of teams
   * @param {string} parentId - Parent team ID (null for root)
   * @returns {Array} Hierarchical tree structure
   */
  buildHierarchy(units, parentId = null) {
    return units
      .filter(unit => unit.parentUnitId === parentId)
      .map(unit => ({
        ...unit,
        children: this.buildHierarchy(units, unit.id)
      }));
  }

  /**
   * Get full path to a team (for breadcrumbs)
   *
   * @param {string} unitId - Team ID
   * @param {Array} allUnits - All teams array (optional, will fetch if not provided)
   * @returns {Promise<Array>} Array of teams from root to target
   */
  async getUnitPath(unitId, allUnits = null) {
    if (!unitId) return [];

    try {
      const unit = await this.getUnit(unitId);

      if (!allUnits) {
        allUnits = await this.getUnits(unit.schoolId);
      }

      const path = [unit];
      let currentUnit = unit;

      while (currentUnit.parentUnitId) {
        const parent = allUnits.find(u => u.id === currentUnit.parentUnitId);
        if (!parent) break;
        path.unshift(parent);
        currentUnit = parent;
      }

      return path;
    } catch (error) {
      console.error('[OrgUnitService] Error building unit path:', error);
      return [];
    }
  }

  /**
   * Invalidate cache for a specific school
   *
   * @param {string} schoolId - School ID
   */
  invalidateSchoolCache(schoolId) {
    this.cache.unitsBySchool.delete(schoolId);
    this.cache.timestamps.delete(schoolId);
    console.log(`[OrgUnitService] Cache invalidated for school ${schoolId}`);
  }

  /**
   * Clear all caches
   */
  clearCache() {
    this.cache.unitsBySchool.clear();
    this.cache.timestamps.clear();
    console.log('[OrgUnitService] All caches cleared');
  }
}

// Export singleton instance
export const organizationalUnitService = new OrganizationalUnitService();
export default organizationalUnitService;
